/**
 * Immersive product hero
 *
 * Full-bleed governed media with a low-anchored copy rail and one dominant
 * action. FmProductHero owns the crop, scrim, responsive stacking, lead
 * interaction, and next-section cue. Routes pass a sealed descriptor.
 */
import React from 'react';
import { FmAssetImage } from './FmAssetImage';
import { FmButton } from './FmButton';
import { FmGrowthChart } from './charts/FmGrowthChart';
import { useFmHeroIntervalMs, useFmMotion } from './FmMotionHelper';
import { HeroChoices, HeroSlideCopy, HeroState, LeadInteraction } from './productHeroCopy';
import { ProductAction } from './ProductAction';
import type {
  FmProductHeroDescriptor,
  FmProductHeroForecast,
  FmProductHeroSlide,
} from './FmProductPage.types';
import { validateFmProductHeroDescriptor } from './FmProductPage.validation';
import './FmProductPage.css';

export interface FmProductHeroProps {
  descriptor: FmProductHeroDescriptor;
  firstChapterId: string;
  'data-testid'?: string;
}

function formatForecastValue(value: number, unit: string): string {
  if (unit === 'EUR') {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0,
    }).format(value);
  }
  return `${new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(value)} ${unit}`;
}

const HeroForecast: React.FC<{
  forecast: FmProductHeroForecast;
  priceTrend?: FmProductHeroDescriptor['priceTrend'];
  activeSlide: FmProductHeroSlide;
}> = ({ forecast, priceTrend, activeSlide }) => {
  const property = `${forecast.copy.propertyLabel} ${forecast.propertyId}`;
  if (forecast.status !== 'ready' || activeSlide === 'choices') {
    if (forecast.status === 'ready') {
      return <HeroChoices forecast={forecast} property={property} />;
    }
    return (
      <section
        className="fm-product-hero__forecast fm-product-hero__forecast--status"
        aria-label={property}
        data-testid={`product-hero-forecast-${forecast.status}`}
      >
        <p className="fm-product-hero__forecast-property">{property}</p>
        <p className="fm-product-hero__forecast-status-title">
          {forecast.status === 'loading'
            ? forecast.copy.loadingLabel
            : forecast.copy.unavailableLabel}
        </p>
        <p className="fm-product-hero__forecast-status-detail">
          {forecast.status === 'loading'
            ? forecast.copy.loadingDetail
            : forecast.copy.unavailableDetail}
        </p>
      </section>
    );
  }
  if (activeSlide === 'harvest-income' && priceTrend) {
    return (
      <section
        className="fm-product-hero__forecast"
        aria-label={priceTrend.regionLabel}
        data-testid="product-hero-price-trend"
      >
        <p className="fm-product-hero__forecast-property">{priceTrend.regionLabel}</p>
        <p className="fm-product-hero__forecast-metric">{forecast.copy.incomeNavLabel} · €/m³</p>
        <div className="fm-product-hero__forecast-chart" aria-label={forecast.copy.incomeNavLabel}>
          <FmGrowthChart
            data={priceTrend.points.map((point) => ({
              week: String(point.week).replace(/^(\d{4})W/, '$1/'),
              N1: typeof point.N1 === 'number' ? point.N1 : null,
              N4: typeof point.N4 === 'number' ? point.N4 : null,
            }))}
            xKey="week"
            xLabel=""
            yLabel="€/m³"
            render="line"
            size="sm"
            surface="on-dark"
            valueDomain="data-extent"
            grid="none"
            series={[
              { key: 'N1', label: priceTrend.pineLabel, color: { by: 'tone', tone: 'teal' } },
              { key: 'N4', label: priceTrend.pulpLabel, color: { by: 'tone', tone: 'accent' } },
            ]}
            formatValue={(value) => `${value.toFixed(2)} €/m³`}
            data-testid="product-hero-price-chart"
          />
        </div>
        <p className="fm-product-hero__forecast-provenance">{priceTrend.sourceLabel}</p>
      </section>
    );
  }
  if (activeSlide === 'harvest-income') {
    return (
      <section
        className="fm-product-hero__forecast fm-product-hero__forecast--status"
        aria-label={property}
      >
        <p className="fm-product-hero__forecast-property">{property}</p>
        <p className="fm-product-hero__forecast-status-title">
          {forecast.copy.unavailableValueLabel}
        </p>
      </section>
    );
  }
  const series = forecast.growth;
  const metricLabel = forecast.copy.growthLabel;
  return (
    <section
      className="fm-product-hero__forecast"
      aria-label={property}
      data-testid="product-hero-forecast-ready"
    >
      <header className="fm-product-hero__forecast-header">
        <p className="fm-product-hero__forecast-property">
          {property} · {forecast.scopeLabel}
        </p>
      </header>
      <p className="fm-product-hero__forecast-metric">
        {metricLabel} · {series.unit}
      </p>
      <div className="fm-product-hero__forecast-chart" aria-label={metricLabel}>
        <FmGrowthChart
          data={series.points.map((point) => ({
            year: point.year,
            value: point.value,
          }))}
          xKey="year"
          xLabel=""
          yLabel={series.unit}
          render="area"
          size="sm"
          surface="on-dark"
          areaEmphasis="subtle"
          showDataPoints
          series={[{ key: 'value', label: metricLabel, color: { by: 'tone', tone: 'teal' } }]}
          formatValue={(value) => formatForecastValue(value, series.unit)}
          data-testid="product-hero-forecast-chart"
        />
      </div>
    </section>
  );
};

const HeroSlideNavigation: React.FC<{
  forecast: FmProductHeroForecast;
  activeSlide: FmProductHeroSlide;
  onSlideChange: (slide: FmProductHeroSlide) => void;
}> = ({ forecast, activeSlide, onSlideChange }) => (
  <nav
    className="fm-product-hero__forecast-controls"
    aria-label={forecast.copy.choicesNavLabel}
    data-testid="product-hero-slide-navigation"
  >
    {(
      [
        ['growth', forecast.copy.growthNavLabel],
        ['choices', forecast.copy.choicesNavLabel],
        ['harvest-income', forecast.copy.incomeNavLabel],
      ] as const
    ).map(([slide, label]) => (
      <FmButton
        key={slide}
        type="button"
        variant={activeSlide === slide ? 'primary' : 'bordered'}
        size="sm"
        aria-pressed={activeSlide === slide}
        onClick={() => onSlideChange(slide)}
      >
        {label}
      </FmButton>
    ))}
  </nav>
);

const HERO_SLIDES: readonly FmProductHeroSlide[] = ['growth', 'choices', 'harvest-income'];

function useHeroCarousel(descriptor: FmProductHeroDescriptor) {
  const [activeSlide, setActiveSlide] = React.useState<FmProductHeroSlide>('growth');
  const [paused, setPaused] = React.useState(false);
  const [hoverPaused, setHoverPaused] = React.useState(false);
  const [focusPaused, setFocusPaused] = React.useState(false);
  const [pageHidden, setPageHidden] = React.useState(
    () => typeof document !== 'undefined' && document.hidden,
  );
  const [timerReset, setTimerReset] = React.useState(0);
  const { canAnimate } = useFmMotion('section');
  const autoplayIntervalMs = useFmHeroIntervalMs();
  const renderedSlides =
    descriptor.slideCopy || descriptor.slideMediaIdentities || descriptor.forecast
      ? HERO_SLIDES
      : (['growth'] as const);
  const carouselEnabled = renderedSlides.length > 1;
  const changeSlide = (slide: FmProductHeroSlide) => {
    setActiveSlide(slide);
    setTimerReset((current) => current + 1);
    if (descriptor.forecast?.status === 'ready' && slide !== 'choices') {
      descriptor.forecast.onMetricChange(slide);
    }
  };
  React.useEffect(() => {
    const handleVisibilityChange = () => setPageHidden(document.hidden);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);
  React.useEffect(() => {
    if (
      !carouselEnabled ||
      paused ||
      hoverPaused ||
      focusPaused ||
      pageHidden ||
      !canAnimate ||
      autoplayIntervalMs === null
    )
      return;
    const timer = window.setTimeout(() => {
      setActiveSlide(
        (current) => HERO_SLIDES[(HERO_SLIDES.indexOf(current) + 1) % HERO_SLIDES.length],
      );
    }, autoplayIntervalMs);
    return () => window.clearTimeout(timer);
  }, [
    activeSlide,
    autoplayIntervalMs,
    canAnimate,
    carouselEnabled,
    focusPaused,
    hoverPaused,
    pageHidden,
    paused,
    timerReset,
  ]);
  return {
    activeSlide,
    changeSlide,
    paused,
    renderedSlides,
    setFocusPaused,
    setHoverPaused,
    setPaused,
  };
}

export const FmProductHero: React.FC<FmProductHeroProps> = ({
  descriptor,
  firstChapterId,
  'data-testid': dataTestId,
}) => {
  validateFmProductHeroDescriptor(descriptor);
  const {
    activeSlide,
    changeSlide,
    paused,
    renderedSlides,
    setFocusPaused,
    setHoverPaused,
    setPaused,
  } = useHeroCarousel(descriptor);
  const headingId = `${descriptor.id}-title`;
  return (
    <section
      id={descriptor.id}
      className="fm-product-hero"
      aria-labelledby={headingId}
      data-fm-nav-theme="dark"
      data-testid={dataTestId ?? 'fm-product-hero'}
    >
      <div className="fm-product-hero__media">
        {renderedSlides.map((slide) => (
          <div
            key={slide}
            className={`fm-product-hero__media-slide${activeSlide === slide ? ' fm-product-hero__media-slide--active' : ''}`}
            aria-hidden={activeSlide !== slide}
          >
            <FmAssetImage
              assetKey={descriptor.slideMediaIdentities?.[slide] ?? descriptor.mediaIdentity}
              cropRole="background"
            />
          </div>
        ))}
      </div>
      <div className="fm-product-hero__scrim" aria-hidden="true" />
      {descriptor.showcaseMediaIdentity ? (
        <div className="fm-product-hero__showcase">
          <FmAssetImage
            assetKey={descriptor.showcaseMediaIdentity}
            cropRole="split"
            data-testid="product-hero-showcase-media"
          />
          {descriptor.showcaseMediaLabel ? (
            <span
              className="fm-product-hero__showcase-label"
              data-testid="product-hero-showcase-label"
            >
              {descriptor.showcaseMediaLabel}
            </span>
          ) : null}
        </div>
      ) : null}
      <div
        className="fm-product-hero__inner"
        onMouseEnter={() => setHoverPaused(true)}
        onMouseLeave={() => setHoverPaused(false)}
        onFocusCapture={() => setFocusPaused(true)}
        onBlurCapture={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget)) setFocusPaused(false);
        }}
      >
        <div className="fm-product-hero__story-stack">
          {renderedSlides.map((slide) => {
            const active = activeSlide === slide;
            return (
              <div
                key={slide}
                className={`fm-product-hero__story-slide${active ? ' fm-product-hero__story-slide--active' : ''}`}
                aria-hidden={!active}
                inert={!active}
              >
                <HeroSlideCopy
                  descriptor={descriptor}
                  slide={slide}
                  headingId={active ? headingId : undefined}
                />
                {descriptor.forecast ? (
                  <HeroForecast
                    forecast={descriptor.forecast}
                    priceTrend={descriptor.priceTrend}
                    activeSlide={slide}
                  />
                ) : null}
              </div>
            );
          })}
        </div>
        {descriptor.primaryAction ? (
          <div className="fm-product-hero__actions">
            <ProductAction action={descriptor.primaryAction} />
            {descriptor.secondaryAction ? (
              <ProductAction action={descriptor.secondaryAction} />
            ) : null}
          </div>
        ) : null}
        <HeroState state={descriptor.state} />
        {descriptor.forecast ? (
          <div className="fm-product-hero__carousel-controls">
            <HeroSlideNavigation
              forecast={descriptor.forecast}
              activeSlide={activeSlide}
              onSlideChange={changeSlide}
            />
            <FmButton
              type="button"
              variant="text"
              size="sm"
              aria-label={
                paused
                  ? (descriptor.carouselPlayLabel ?? 'Play carousel')
                  : (descriptor.carouselPauseLabel ?? 'Pause carousel')
              }
              onClick={() => setPaused((value) => !value)}
              data-testid="product-hero-carousel-toggle"
            >
              <span aria-hidden="true">{paused ? '▶' : 'Ⅱ'}</span>
            </FmButton>
          </div>
        ) : null}
        {descriptor.leadInteraction ? (
          <LeadInteraction interaction={descriptor.leadInteraction} />
        ) : null}
      </div>
      <a className="fm-product-hero__next" href={`#${firstChapterId}`}>
        <span>{descriptor.nextSectionLabel}</span>
        <span aria-hidden="true">↓</span>
      </a>
    </section>
  );
};

FmProductHero.displayName = 'FmProductHero';

export default FmProductHero;
